import { FeedbackFormSpec, FormModelSpec } from "@coaching-culture/types";
import {
  Alert,
  Box,
  FormEditor,
  FormInput,
  FormToggle,
  Loader,
  Panel,
  Text,
} from "@coaching-culture/ui";
import axios from "axios";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import { SurveyPlayer } from "components/SurveyPlayer";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";

export function EditFeedbackForm() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<FeedbackFormSpec | null>(null);
  const [previewing, setPreviewing] = useState<FormModelSpec | null>(null);
  const [detailsDirty, setDetailsDirty] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (id === "create") {
      setForm({
        name: "",
        published: false,
        form: {
          name: "",
          items: [],
        },
      });
    } else {
      axios.get(`/api/feedback/forms/${id}`).then(({ data }) => {
        setForm(data);
      });
    }
  }, [id]);

  if (form == null) {
    return <Loader />;
  }

  const submit = (newForm: FormModelSpec) => {
    const spec = {
      ...form,
      form: newForm,
    };

    if (spec.form.items.length === 0) {
      return window.alert("A form cannot have 0 items");
    } else if (spec.form.items[spec.form.items.length - 1].type === "heading") {
      return window.alert(
        "The last item of a form cannot be a section heading"
      );
    }

    setDetailsDirty(false);

    if (id === "create") {
      axios.post("/api/feedback/forms", spec).then(() => {
        history.push("/success/feedback/forms");
      });
    } else {
      axios.put(`/api/feedback/forms/${id}`, spec).then(() => {
        history.push("/success/feedback/forms");
      });
    }
  };

  return (
    <>
      {previewing && (
        <SurveyPlayer
          form={previewing}
          onCompletion={() => setPreviewing(null)}
          onClose={() => setPreviewing(null)}
        />
      )}
      <CenterColumn
        style={{ display: previewing == null ? undefined : "none" }}
      >
        <PageHeader
          text={`${id === "create" ? "Create" : "Edit"} Feedback Form`}
          subtitle="Configure a feedback form for use in a campaign"
          backUrl="/success/feedback/forms"
        />
        <Panel p={3}>
          <Box>
            {id !== "create" && (
              <Alert color="danger" mb={3}>
                <Text fontWeight="600" mb={1}>
                  Caution
                </Text>
                <p>
                  Be careful when editing forms used in existing surveys.
                  Modifying existing questions may result in invalid or
                  incomplete data.
                </p>
              </Alert>
            )}
            <Text fontSize={4} mb={2}>
              Details
            </Text>
            <FormInput
              label="Feedback Form Name"
              name="name"
              mb={2}
              type="text"
              value={form.name}
              onChange={(ev) => {
                setDetailsDirty(true);
                setForm((old) => ({ ...old, name: ev.target.value }));
              }}
            />
            <FormToggle
              mb={3}
              label="Published"
              helpText="This allows forms to be used in a Campaign but do not affect existing Campaigns"
              value={form.published}
              onChange={(ev) => {
                setDetailsDirty(true);
                setForm((old) => ({ ...old, published: ev }));
              }}
            />
            <FormEditor
              form={form.form}
              onSave={submit}
              dirty={detailsDirty}
              onPreview={setPreviewing}
              onChange={(form) =>
                setForm((old) => ({
                  ...old,
                  form,
                }))
              }
            />
          </Box>
        </Panel>
      </CenterColumn>
    </>
  );
}
